import React, { useEffect, useState } from 'react'
import { MasonryGallery } from '../components/MasonryGallery'
import { WordCloudSearch } from '../components/WordCloudSearch'

interface Gallery {
  title: string
  subtitle: string
  category: string
  location: string
  venue: string
  thumbnail: string
  keywords: string[]
  images: Array<{
    id: string
    filename: string
    thumbnail: string
    fullsize: string
    keywords?: string[]
  }>
}

type GalleryData = Record<string, Gallery>

interface MasonryImage {
  id: string
  thumbnail: string
  fullsize: string
  galleryId: string
  galleryTitle: string
  keywords: string[]
}

interface SearchResult {
  galleryId: string
  gallery: Gallery
  matchingImages: any[]
}

export const PortfolioPage: React.FC = () => {
  const [galleries, setGalleries] = useState<GalleryData>({})
  const [allImages, setAllImages] = useState<MasonryImage[]>([])
  const [filteredImages, setFilteredImages] = useState<MasonryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [isSearchActive, setIsSearchActive] = useState(false)
  const [activeKeywords, setActiveKeywords] = useState<string[]>([])

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch('/data/galleries.json')
        const data: GalleryData = await response.json()
        setGalleries(data)
        
        // Flatten all images from all galleries into a single array
        const flatImages: MasonryImage[] = []
        Object.entries(data).forEach(([galleryId, gallery]) => {
          gallery.images?.forEach(image => {
            // Combine image keywords with gallery keywords
            const combinedKeywords = [
              ...(image.keywords || []),
              ...(gallery.keywords || [])
            ]
            
            flatImages.push({
              id: image.id,
              thumbnail: image.thumbnail,
              fullsize: image.fullsize,
              galleryId,
              galleryTitle: gallery.title,
              keywords: [...new Set(combinedKeywords)] // Remove duplicates
            })
          })
        })
        
        setAllImages(flatImages)
        setFilteredImages(flatImages)
      } catch (error) {
        console.error('Error fetching galleries:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGalleries()
  }, [])

  const handleSearchResults = (results: SearchResult[]) => {
    // Flatten search results into images array
    const searchImages: MasonryImage[] = []
    results.forEach(result => {
      result.matchingImages.forEach(image => {
        const combinedKeywords = [
          ...(image.keywords || []),
          ...(result.gallery.keywords || [])
        ]
        
        searchImages.push({
          id: image.id,
          thumbnail: image.thumbnail,
          fullsize: image.fullsize,
          galleryId: result.galleryId,
          galleryTitle: result.gallery.title,
          keywords: [...new Set(combinedKeywords)]
        })
      })
    })
    
    setFilteredImages(searchImages)
    setIsSearchActive(searchImages.length !== allImages.length)
  }

  const handleKeywordSearch = (keywords: string[]) => {
    setActiveKeywords(keywords)
    if (keywords.length === 0) {
      setFilteredImages(allImages)
      setIsSearchActive(false)
    } else {
      // Filter images that contain ALL selected keywords (AND logic)
      const filtered = allImages.filter(image => 
        keywords.every(keyword => 
          image.keywords.some(imageKeyword => 
            imageKeyword.toLowerCase().includes(keyword.toLowerCase())
          )
        )
      )
      setFilteredImages(filtered)
      setIsSearchActive(true)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Complete Portfolio
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our complete collection of fine art wedding and engagement photography. 
            Use the interactive word cloud below to discover images by style, venue, or mood.
          </p>
        </div>
      </section>

      {/* Word Cloud Search */}
      <section className="bg-white border-b border-gray-100">
        <WordCloudSearch 
          galleries={galleries} 
          onSearchResults={handleSearchResults}
          onKeywordSearch={handleKeywordSearch}
        />
      </section>

      {/* Search Status */}
      {isSearchActive && (
        <section className="py-6 px-4 sm:px-6 lg:px-8 bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Showing {filteredImages.length} {filteredImages.length === 1 ? 'image' : 'images'}
                  {activeKeywords.length > 0 && (
                    <span className="ml-2">
                      for: {activeKeywords.map((keyword, index) => (
                        <span key={keyword} className="inline-flex items-center">
                          <span className="font-medium text-gray-900">{keyword}</span>
                          {index < activeKeywords.length - 1 && <span className="mx-1 text-gray-400">+</span>}
                        </span>
                      ))}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => {
                  setFilteredImages(allImages)
                  setIsSearchActive(false)
                  setActiveKeywords([])
                }}
                className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
              >
                Clear search
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Masonry Gallery */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {filteredImages.length > 0 ? (
            <MasonryGallery 
              images={filteredImages} 
              searchKeywords={activeKeywords}
            />
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg mb-4">
                No images found matching your search criteria.
              </p>
              <button
                onClick={() => {
                  setFilteredImages(allImages)
                  setIsSearchActive(false)
                  setActiveKeywords([])
                }}
                className="text-gray-900 hover:text-gray-700 underline transition-colors"
              >
                View all images
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Portfolio Stats */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-3xl font-light text-gray-900 mb-2">
                {allImages.length}
              </div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">
                Total Images
              </div>
            </div>
            <div>
              <div className="text-3xl font-light text-gray-900 mb-2">
                {Object.keys(galleries).length}
              </div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">
                Gallery Collections
              </div>
            </div>
            <div>
              <div className="text-3xl font-light text-gray-900 mb-2">
                {new Set(Object.values(galleries).map(g => g.venue)).size}
              </div>
              <div className="text-sm text-gray-600 uppercase tracking-wide">
                Unique Venues
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
